import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Select,
  InputNumber,
  Button,
  Collapse,
  Space,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";

const { Option } = Select;

const TransferToInventoryModal = ({
  visible,
  onCancel,
  onTransfer,
  itemsData = {},
  selectedRows = [],
  accessLevel,
}) => {
  const [form] = Form.useForm();
  const [activeKeys, setActiveKeys] = useState([]);

  // Use useWatch to make the component re-render when the 'materialID' field changes
  const materialID = Form.useWatch("materialID", form);

  const allMaterials = Object.entries(itemsData).map(([id, item]) => ({
    materialID: id,
    ...item,
  }));

  const selectedMaterial = allMaterials.find(
    (item) => item.materialID === materialID
  );

  useEffect(() => {
    if (visible) {
      // Set initial state
      if (selectedRows && selectedRows.length > 0) {
        const initialFormValues = {
          materialID: undefined, // Initial material is not selected
          unit: undefined,
          items: selectedRows.map((row) => ({
            purchaseOrderID: row.purchaseOrderID,
            projectID: row.projectID,
            unitPrice: 0,
            newQuantity: 1,
          })),
        };
        form.setFieldsValue(initialFormValues);
        setActiveKeys(selectedRows.map((_, index) => index.toString()));
      } else {
        form.setFieldsValue({
          materialID: undefined,
          unit: undefined,
          items: [],
        });
        setActiveKeys([]);
      }
    }
  }, [visible, selectedRows, form]);

  // Handle material selection and auto-select unit
  const handleMaterialChange = (id) => {
    const selectedItem = itemsData[id];
    if (selectedItem) {
      form.setFieldsValue({
        unit: selectedItem.unit,
      });
    }
  };

  const handleSubmit = (values) => {
    if (onTransfer) {
      const transferData = {
        items: values.items.map((item) => ({
          itemID: values.materialID,
          unit: values.unit,
          purchaseOrderID: item?.purchaseOrderID,
          unitPrice: item.unitPrice,
          projectID: item?.projectID,
          newQuantity: item.newQuantity,
        })),
      };
      onTransfer(transferData);
    }
    form.resetFields();
    onCancel();
  };

  const handleCancel = () => {
    form.setFieldsValue({
      materialID: undefined,
      unit: undefined,
      items: [],
    });
    form.resetFields();
    setActiveKeys([]);
    onCancel();
  };

  return (
    <Modal
      title="Transfer to Inventory"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={() => {
            form.submit();
          }}
        >
          Transfer
        </Button>,
      ]}
      width={800}
      centered
      destroyOnClose={true}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="materialID"
          label="Select Material"
          rules={[{ required: true, message: "Please select a material" }]}
        >
          <Select
            placeholder="Choose a material"
            showSearch
            optionFilterProp="children"
            onChange={handleMaterialChange}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {allMaterials.map((item) => (
              <Option key={item.materialID} value={item.materialID}>
                {item.materialName}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="unit"
          label="Unit"
          rules={[{ required: true, message: "Unit is required" }]}
        >
          <Select placeholder="Unit will be auto-selected" disabled>
            {selectedMaterial && (
              <Option value={selectedMaterial.unit}>
                {selectedMaterial.unit}
              </Option>
            )}
          </Select>
        </Form.Item>

        <Collapse
          activeKey={activeKeys}
          onChange={setActiveKeys}
          ghost
          className="bg-light bg-opacity-75 border border-1 mb-3"
        >
          {selectedRows.map((row, index) => (
            <Collapse.Panel
              key={index.toString()}
              header={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span className="fw-bold">{row?.title}</span>
                </div>
              }
            >
              {/* These hidden fields ensure purchaseOrderID and projectID are tracked by the form */}
              <Form.Item name={["items", index, "purchaseOrderID"]} hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item name={["items", index, "projectID"]} hidden>
                <InputNumber />
              </Form.Item>

              <Form.Item
                name={["items", index, "unitPrice"]}
                label="Unit Price ($)"
                rules={[
                  { required: true, message: "Please enter unit price" },
                  {
                    type: "number",
                    min: 0,
                    message: "Unit price must be a positive number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="0.00"
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>

              <Form.Item
                name={["items", index, "newQuantity"]}
                label="Quantity"
                rules={[
                  { required: true, message: "Please enter quantity" },
                  {
                    type: "number",
                    min: 1,
                    message: "Quantity must be at least 1",
                  },
                ]}
              >
                <InputNumber
                  placeholder="1"
                  style={{ width: "100%" }}
                  min={1}
                  step={1}
                  precision={0}
                />
              </Form.Item>
            </Collapse.Panel>
          ))}
        </Collapse>
      </Form>
    </Modal>
  );
};

export default TransferToInventoryModal;
